import React from 'react';

import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';
import DoneIcon from '@material-ui/icons/Done';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

import * as StampCardService from '../../api/stampCardService';

const useStoreStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    width: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
  },
  visited: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
    marginBottom: theme.spacing(2),
  },
  remaining: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginRight: 4,
  },
  subtitleBold: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightBold,
  },
  info: {
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(),
    padding: '0 16px',
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  showDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 16px',
  },
  showDetailButton: {
    color: theme.palette.text.secondary,
  },
  grow: {
    flexGrow: 1,
  },
  finePrint: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  storeDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 16px',
  },
  done: {
    color: '#008000',
    stroke: '#008000',
    strokeWidth: 1,
  },
  pending: {
    color: theme.palette.primary.main,
  },
  storeContainer: {
    padding: '0 16px',
  },
  noStoreDetailContainer: {
    padding: '0',
  },
}));

export function StoreDetail({
  store,
  visited,
}: {
  store: StampCardService.StoreInfo;
  visited?: boolean;
}) {
  const classes = useStoreStyles();

  return (
    <div className={classes.storeDetailContainer} key={store.ID}>
      {visited ? (
        <DoneIcon fontSize="medium" className={classes.done} />
      ) : (
        <FiberManualRecordIcon fontSize="small" className={classes.pending} />
      )}
      <Typography variant="body2" className={classes.info}>
        <Grid container alignItems="center" spacing={1}>
          {visited && store.Count > 1 ? (
            <Grid item xs={9}>
              {store.Name}
            </Grid>
          ) : (
            <Grid item xs={12}>
              {store.Name}
            </Grid>
          )}
          {visited && store.Count > 1 && (
            <Grid item xs={3} style={{ whiteSpace: 'nowrap' }}>
              ({store.Count} times)
            </Grid>
          )}
        </Grid>
      </Typography>
    </div>
  );
}

export function VisitedStoreContainer({
  visitedStoreList,
  remainingStoreList,
}: {
  visitedStoreList: StampCardService.StoreInfo[];
  remainingStoreList: StampCardService.StoreInfo[];
}) {
  const classes = useStoreStyles();

  return (
    <div className={classes.root}>
      <div className={classes.cardContainer}>
        <div className={classes.storeContainer}>
          <Typography variant="h4" className={classes.visited}>
            Visited
          </Typography>
          {visitedStoreList.length === 0 && (
            <div className={classes.noStoreDetailContainer}>
              <Typography variant="body2" className={classes.info}>
                Visit one of the locations below!
              </Typography>
            </div>
          )}
          <>
            {visitedStoreList.map((store) => (
              <StoreDetail key={store.ID} store={store} visited />
            ))}
          </>
        </div>
        {remainingStoreList.length === 0 && (
          <div className={classes.storeContainer}>
            <Typography variant="h4" className={classes.remaining}>
              Remaining
            </Typography>
            <div className={classes.noStoreDetailContainer}>
              <Typography variant="body2" className={classes.info}>
                All locations visited!
              </Typography>
            </div>
          </div>
        )}
        {remainingStoreList.length > 0 && (
          <div className={classes.storeContainer}>
            <Typography variant="h4" className={classes.remaining}>
              Remaining
            </Typography>
            <>
              {remainingStoreList.map((store) => (
                <StoreDetail key={store.ID} store={store} />
              ))}
            </>
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    height: 30,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  box: {
    flex: 1,
    display: 'flex',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(4),
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  selectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: 'left',
  },
  unselectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
  },
  homeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(),
    marginTop: theme.spacing(4),
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
  },

  listItemContainer: {
    marginLeft: 0,
    transition: 'none !important',
    backgroundColor: 'inherit !important',
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  storeVisitedList: StampCardService.StoreVisitList;
};

export default function ViewMoreVisitedDialog({
  onClose,
  open,
  storeVisitedList,
}: Props) {
  const classes = useStyles();

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <div className={classes.toolbar}>
        <div style={{ flexGrow: 1 }} />
        <IconButton onClick={handleClose}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <Grid item xs={12} className={classes.root}>
        <VisitedStoreContainer
          visitedStoreList={storeVisitedList.visitedStores}
          remainingStoreList={storeVisitedList.nonVisitedStores}
        />
      </Grid>
    </Dialog>
  );
}
